import { useEffect, useMemo } from 'react';

import useSnackbar from 'src/hooks/useSnackbar';
import { useGetPayPeriodsQuery } from 'src/services/PayPeriods';
import { PayPeriod } from 'src/types/resources/PayPeriod';
import { getCurrentPeriod, getPreviousPeriod } from 'src/utils/payPeriods';
import { getPayPeriodToday } from 'src/utils/payPeriodTime';

type UsePayPeriods = {
  periods: PayPeriod[];
  currentPeriod: PayPeriod | undefined;
  previousPeriod: PayPeriod | undefined;
  weeklyPayPeriodsStartDate: string | undefined;
  isLoading: boolean;
  isError: boolean;
};

const EMPTY_PERIODS: PayPeriod[] = [];
const PAY_PERIODS_ERROR_SNACKBAR_KEY = 'payPeriodsLoadError';
const NO_CURRENT_PAY_PERIOD_SNACKBAR_KEY = 'noCurrentPayPeriod';

const usePayPeriods = (): UsePayPeriods => {
  const { enqueueErrorSnackbar } = useSnackbar();
  const {
    data: fetchedPeriods,
    isLoading,
    isSuccess,
    isError,
  } = useGetPayPeriodsQuery(undefined, { refetchOnFocus: true });
  const data = fetchedPeriods?.results ?? EMPTY_PERIODS;
  const weeklyPayPeriodsStartDate = fetchedPeriods?.weeklyPayPeriodsStartDate ?? undefined;

  const today = weeklyPayPeriodsStartDate ? getPayPeriodToday(weeklyPayPeriodsStartDate) : undefined;
  const startedPeriods = useMemo(
    () => (today ? data.filter(period => period.startDate <= today) : EMPTY_PERIODS),
    [data, today],
  );

  const currentPeriod = today ? getCurrentPeriod(data, today) : undefined;
  const previousPeriod = currentPeriod && getPreviousPeriod(startedPeriods, currentPeriod);
  const isLoadFailed = isError && !fetchedPeriods;
  const isCurrentPeriodMissing = isSuccess && !currentPeriod;

  useEffect(() => {
    if (isLoadFailed) {
      enqueueErrorSnackbar('Failed to load pay periods', {
        key: PAY_PERIODS_ERROR_SNACKBAR_KEY,
        preventDuplicate: true,
      });
    }
  }, [isLoadFailed]);

  useEffect(() => {
    if (isCurrentPeriodMissing) {
      enqueueErrorSnackbar('No open pay period found', {
        key: NO_CURRENT_PAY_PERIOD_SNACKBAR_KEY,
        preventDuplicate: true,
      });
    }
  }, [isCurrentPeriodMissing]);

  return { periods: startedPeriods, currentPeriod, previousPeriod, weeklyPayPeriodsStartDate, isLoading, isError };
};

export default usePayPeriods;
